import { Injectable } from '@angular/core';
import { AlertService, IAlertType } from "./alert.service";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { InventoryPayload, InventoryResult, Warehouse, WarehouseSelectable, WarehouseSerialSearch, WarehouseStockSerial, WebserviceResponse } from "../interfaces";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import { restBaseUrl } from "../utils/rest-url";

export interface WarehouseFilter {
  project_id?: string;
  job_id?: number;
  order_id?: number;
  position?: number;
  part?: number;
  movement_type?: 'incoming' | 'outgoing';
}

@Injectable({
  providedIn: 'root'
})
export class WarehouseStoreService {
  warehouse: Warehouse[] = [];

  private endpoint = restBaseUrl('/warehouse');

  constructor(
    private http: HttpClient,
    private alertDispatcher: AlertService) {
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }

    this.alertDispatcher.alertConfig.message = errorMessage;
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;

    return throwError(errorMessage);
  }

  public getPositionsWarehouse(projectId: string, jobId: number, orderId: number, position: number, part: number): Observable<WebserviceResponse<Warehouse[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectId,
        order_id: orderId,
        job_id: jobId,
        position: position,
        part: part
      }
    });
    return this.http.get<WebserviceResponse<Warehouse[]>>(this.endpoint + '/get_warehouses',
      {params: options}).pipe(retry(3), catchError(this.handleError));
  }

  public getAllWarehouses(filter: WarehouseFilter = {}): Observable<WebserviceResponse<Warehouse[]>> {
    let params = new HttpParams();
    if (filter.project_id) params = params.set('project_id', filter.project_id);
    if (filter.job_id) params = params.set('job_id', filter.job_id);
    if (filter.order_id) params = params.set('order_id', filter.order_id);
    if (filter.position != null) params = params.set('position', filter.position);
    if (filter.part != null) params = params.set('part', filter.part);
    if (filter.movement_type) params = params.set('movement_type', filter.movement_type);
    return this.http.get<WebserviceResponse<Warehouse[]>>(this.endpoint + '/get_warehouses',
      {params}).pipe(retry(3), catchError(this.handleError));
  }

  public addToWarehouse(warehouse: Partial<Warehouse>): Observable<WebserviceResponse<boolean>> {
    return this.http.post<WebserviceResponse<boolean>>(
      `${this.endpoint}/add_warehouse`,
      warehouse,
      { responseType: 'json'}
    ).pipe(retry(3), catchError(this.handleError));
  }

  /**
   * Inventur: meldet den gezaehlten Bestand.
   *
   * Der Server rechnet die Differenz zum errechneten Bestand und bucht sie
   * als inventory_in oder inventory_out. Auch eine Differenz von 0 wird
   * gebucht -- sonst laesst sich spaeter nicht belegen, dass gezaehlt wurde.
   *
   * Kein retry(): das hier ist eine Buchung, kein Abruf. Ein Wiederholen
   * nach einer unklaren Antwort erzeugte eine zweite Inventurzeile.
   */
  public addInventory(payload: InventoryPayload): Observable<InventoryResult> {
    return this.http.post<InventoryResult>(
      `${this.endpoint}/inventory`,
      payload,
      { responseType: 'json' }
    ).pipe(catchError(this.handleError));
  }

  public updateWarehouse(payload: Partial<Warehouse> & { id: number }): Observable<WebserviceResponse<boolean>> {
    return this.http.put<WebserviceResponse<boolean>>(
      `${this.endpoint}/update_warehouse`,
      payload,
      { responseType: 'json' }
    ).pipe(retry(3), catchError(this.handleError));
  }

  public getWarehouseSelectable(projectId: string, orderId?: number, jobId?: number): Observable<WebserviceResponse<WarehouseSelectable[]>> {
    let params = new HttpParams().set('project_id', projectId);
    if (orderId != null && orderId > 0) params = params.set('order_id', orderId);
    if (jobId   != null && jobId   > 0) params = params.set('job_id',   jobId);
    return this.http.get<WebserviceResponse<WarehouseSelectable[]>>(
      `${this.endpoint}/get_warehouse_selectable`, { params }
    ).pipe(retry(3), catchError(this.handleError.bind(this)));
  }

  public getStockByDrawing(technicalDrawing: string): Observable<WebserviceResponse<WarehouseStockSerial[]>> {
    const params = new HttpParams().set('technical_drawing', technicalDrawing);
    return this.http.get<WebserviceResponse<WarehouseStockSerial[]>>(
      `${this.endpoint}/get_stock_by_drawing`, { params }
    ).pipe(retry(3), catchError(this.handleError.bind(this)));
  }

  public searchSerial(term: string): Observable<WebserviceResponse<WarehouseSerialSearch[]>> {
    const params = new HttpParams().set('term', term);
    return this.http.get<WebserviceResponse<WarehouseSerialSearch[]>>(
      `${this.endpoint}/search_serial`, { params }
    ).pipe(retry(3), catchError(this.handleError.bind(this)));
  }

  public deleteFromWarehouse(warehouseID: number): Observable<WebserviceResponse<boolean>> {
    return this.http.delete<WebserviceResponse<boolean>>(
      `${this.endpoint}/delete_warehouse?id=${warehouseID}`,
      { responseType: 'json'}
    ).pipe(retry(3), catchError(this.handleError));
  }
}
